import { Checkbox, Spacer } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPatient } from '@common/interfaces/patient';
import type { LabResult } from '@frontend/components/interfaces/labresult';
import type { ValidationErrors } from '@frontend/utils';
import { ProfessionalSummaryDataType } from '@common/enums/professionalSummaryDataType';
import { getLanguage } from '@common/translate/translate';
import {
    LinkOnClick,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { LabResultsCanAdd } from '@frontend/components/pages/customControls/utils/v1/labResults/LabResultsCanAdd';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import { isHisDataAvailable } from '@frontend/services/api/patientsApiService';
import { translateKey } from '@frontend/translate';
import type { LabResultRecord, LabResults } from './labResults';
import { ProfessionalSummaryDataPlaceholder } from '../../../../professionalSummaryData/ProfessionalSummaryDataPlaceholder';
import {
    ProfessionalSummaryDataAction,
    ProfessionalSummaryDataSection,
} from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';
import {
    filterAndPrepareLabResultsForSaving,
    loadProcessActivityLabResults,
    loadRequestLabResults,
} from './labResults';

interface ILabResultsAddEditProps {
    canAdd?: boolean;
    errors?: ValidationErrors;
    onChange: (labResults: LabResult[], validateKeys?: string | string[]) => void | Promise<void>;
    patient: IPatient;
    processActivityId?: string | null;
    requestId?: string;
}

export function LabResultsAddEdit(props: ILabResultsAddEditProps) {
    const { canAdd, errors, patient } = props;
    const translate = getLanguage();
    const [labResults, setLabResults] = useState<LabResults>();
    const [areLabResultsLoaded, setAreLabResultsLoaded] = useState<boolean>(false);
    const [hasHisLabResults, setHasHisLabResults] = useState<boolean>(false);
    const [validationKeys, setValidationKeys] = useState<string[]>([]);

    const [areDiagnosticDeterminationsOpen, setAreDiagnosticDeterminationsOpen] = useState(false);
    const [selectedLabResultsCount, setSelectedLabResultsCount] = useState<number>(0);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);

    useEffect(() => {
        checkIfDataFromHis();
        loadLabResults();
    }, []);

    useEffect(() => {
        setSelectedLabResultsCount(getNumberOfLabResultsSelected());
        props.onChange(filterAndPrepareLabResultsForSaving(labResults), validationKeys || 'labResults');
    }, [labResults]);

    return (
        <>
            {canAdd && !hasHisLabResults ? (
                <LabResultsCanAdd
                    errors={errors}
                    labResults={labResults}
                    setLabResults={setLabResults}
                    setValidationKeys={setValidationKeys}
                    validationKeys={validationKeys}
                />
            ) : null}

            {hasHisLabResults ? (
                <ProfessionalSummaryDataSection
                    extraHeaderElements={
                        patientHasLabResults() ? (
                            <Spacer
                                direction="horizontal"
                                spacing="small"
                            >
                                <LinkOnClick
                                    onClick={async () => {
                                        const action = areAllLabResultsSelected()
                                            ? ProfessionalSummaryDataAction.skip
                                            : ProfessionalSummaryDataAction.pick;
                                        const updateLabResults = labResults
                                            ? labResults.map((labResult) => {
                                                  if (labResult) {
                                                      return labResult.set('action', action);
                                                  }
                                              })
                                            : undefined;

                                        await onChange(updateLabResults as LabResults);
                                    }}
                                >
                                    {translate.professionalSummary.all}
                                </LinkOnClick>
                            </Spacer>
                        ) : undefined
                    }
                    isOpen={areDiagnosticDeterminationsOpen}
                    onToggle={() => setAreDiagnosticDeterminationsOpen(!areDiagnosticDeterminationsOpen)}
                    professionalSummaryDataType={ProfessionalSummaryDataType.diagnosticDeterminations}
                    selectionCount={selectedLabResultsCount}
                >
                    {patientHasLabResults() && (
                        <ProfessionalSummaryDataTable>
                            <TableHead>
                                <TableRow>
                                    <TableHeader></TableHeader>
                                    <TableHeader>{translate.labResults.description}</TableHeader>
                                    <TableHeader align="right">{translate.labResults.valueValue}</TableHeader>
                                    <TableHeader align="right">{translate.labResults.valueUnit}</TableHeader>
                                    <TableHeader align="right">{translate.common.date}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>{setLabResultsRows()}</TableBody>
                        </ProfessionalSummaryDataTable>
                    )}

                    <ProfessionalSummaryDataPlaceholder
                        hasContent={patientHasLabResults()}
                        isLoading={!areLabResultsLoaded}
                        loadFailed={loadFailed}
                        messageFailed={translateKey(
                            'professionalSummary.loadError',
                            translateKey(`professionalSummary.${ProfessionalSummaryDataType.diagnosticDeterminations}`)
                        )}
                        messageNotFound={translate.professionalSummary.noLabResultsFound}
                    />
                </ProfessionalSummaryDataSection>
            ) : null}
        </>
    );

    async function onChange(changedLabResults: LabResults) {
        setLabResults(changedLabResults);
        await props.onChange(filterAndPrepareLabResultsForSaving(changedLabResults));
    }

    function setLabResultsRows() {
        if (!labResults || !hasHisLabResults) {
            return;
        }

        const labResultsCheckboxes = [];

        for (const [key, labResult] of labResults.entrySeq().toArray() as [string, LabResultRecord][]) {
            labResultsCheckboxes.push(
                <TableRow key={labResult.id}>
                    <TableData style={{ width: '36px', minWidth: '36px' }}>
                        <Checkbox
                            checked={labResult.action === ProfessionalSummaryDataAction.pick}
                            onChange={async (event) => {
                                await onChange(
                                    labResults.setIn(
                                        [key, 'action'],
                                        event.target.checked
                                            ? ProfessionalSummaryDataAction.pick
                                            : ProfessionalSummaryDataAction.skip
                                    )
                                );
                            }}
                        />
                    </TableData>
                    <TableData>{labResult.codeDisplayName}</TableData>
                    <TableData
                        align="right"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {labResult.valueValue}
                    </TableData>
                    <TableData
                        align="right"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {labResult.valueUnit}
                    </TableData>
                    <TableData
                        align="right"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        <NullableFormattedDate value={labResult.startDate} />
                    </TableData>
                </TableRow>
            );
        }

        return labResultsCheckboxes;
    }

    function areAllLabResultsSelected() {
        return labResults
            ? labResults.every((labResult) => labResult?.action === ProfessionalSummaryDataAction.pick)
            : false;
    }

    function getNumberOfLabResultsSelected() {
        return labResults
            ? labResults.filter((labResult) => labResult?.action === ProfessionalSummaryDataAction.pick).size
            : 0;
    }

    function patientHasLabResults() {
        return areLabResultsLoaded && labResults !== undefined && labResults.size > 0;
    }

    async function checkIfDataFromHis() {
        setHasHisLabResults(await isHisDataAvailable(patient.id));
    }

    function setActionPickForAddedInitialLabResults(initialLabResults: LabResults) {
        for (const [key] of initialLabResults.entrySeq().toArray() as [string, LabResultRecord][]) {
            initialLabResults = initialLabResults.setIn([key, 'action'], ProfessionalSummaryDataAction.pick);
        }

        return initialLabResults;
    }

    async function loadLabResults() {
        let initialLabResults;

        if (props.processActivityId) {
            try {
                initialLabResults = await loadProcessActivityLabResults(patient, props.processActivityId);
            } catch (error) {
                setLoadFailed(true);
            }
        } else {
            try {
                initialLabResults = await loadRequestLabResults(patient, props.requestId);
            } catch (error) {
                setLoadFailed(true);
            }
        }

        if (canAdd && !hasHisLabResults && initialLabResults) {
            initialLabResults = setActionPickForAddedInitialLabResults(initialLabResults);
        }

        setLabResults(initialLabResults);
        setAreLabResultsLoaded(true);
        setLoadFailed(false);
    }
}
